import { type ClassValue, clsx } from 'clsx';
import { format, isBefore, parseISO } from 'date-fns';
import i18n, { t } from 'i18next';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatDate = ({ dateFormat, date }: { dateFormat: string; date?: string | number | Date | null }) => {
  if (!date) return t('preview.empty.date');
  const parsed = typeof date === 'string' ? parseISO(date) : new Date(date);
  return format(parsed, dateFormat);
};

export const formatNumber = (value: number, options?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'decimal',
    notation: 'standard',
    maximumFractionDigits: 2,
    ...options,
  }).format(value);

export const formatMoney = (value: number, compact = false) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: 2,
  }).format(value);

export const checkDateIsLessThanCurrentDate = (endDate: string) => isBefore(new Date(endDate), new Date());

export const stripHtmlTags = (html: string): string => html.replace(/<[^>]*>/g, '');

export const hasTextContent = (html: string | undefined | null): boolean =>
  !!html && stripHtmlTags(html).trim().length > 0;

const API_LOCALE_MAP: Record<string, string> = {
  pt: 'pt-br',
  en: 'en-us',
  es: 'es-es',
};

export function getAcceptLanguage(): string {
  const lang = i18n.language?.split('-')[0] ?? 'pt';
  return API_LOCALE_MAP[lang] ?? 'pt-br';
}

export const formatPhone = ({ phone, isFormatted }: { phone: string; isFormatted?: boolean }) => {
  const value = isFormatted ? phone.replace(/\D/g, '') : phone;
  const country = COUNTRY_CONFIGS.find((c) => value.startsWith(c.countryCode));
  if (!country) return phone;
  const countryCode = country.countryCode;
  const areaStart = countryCode.length;
  const areaEnd = areaStart + country.areaLength;
  const areaCode = value.slice(areaStart, areaEnd);
  const phoneNumber = value.slice(areaEnd);
  const firstPart = phoneNumber.slice(0, phoneNumber.length - 4);
  const secondPart = phoneNumber.slice(-4);
  return `+${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
};

type CountryConfig = {
  countryCode: string;
  areaLength: number;
};

const COUNTRY_CONFIGS: CountryConfig[] = [
  { countryCode: '55', areaLength: 2 },
  { countryCode: '33', areaLength: 1 },
  { countryCode: '49', areaLength: 2 },
  { countryCode: '1', areaLength: 3 },
];
